import React, { useState, useEffect } from 'react';
import { fetchResumeData } from '../../../fetchProjects';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DownloadSection = () => {
  const [downloadCount, setDownloadCount] = useState(1247);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState("June 29, 2025");

  useEffect(() => {
    const fetchDownloads = async () => {
      try {
        const data = await fetchResumeData();
        const filtered = data.filter(item => item.type === 'download');
        if (filtered.length > 0) {
          setDownloads(filtered);
          
          // Use the latest date from the actual download objects, not the whole resume
          const recentItem = filtered.reduce((latest, current) => {
             if (!current.date_value) return latest;
             if (!latest) return current.date_value;
             return new Date(current.date_value) > new Date(latest) ? current.date_value : latest;
          }, null);
          
          if (recentItem) {
            const d = new Date(recentItem);
            if (!isNaN(d.getTime())) {
               setLastUpdated(d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
            }
          }
        } else {
          setDownloads([]);
        }
      } catch (err) {
        console.error('Failed to fetch downloads:', err);
        setDownloads([]);
      } finally {
        setLoading(false);
      }
    };
    
    const fetchDownloadCount = async () => {
      try {
        const res = await fetch('https://spaceportfolio-api.rajansudhan0.workers.dev/api/settings');
        const data = await res.json();
        if (data.resume_download_count) {
          setDownloadCount(parseInt(data.resume_download_count, 10));
        }
      } catch (err) {
        console.error('Failed to fetch global download count:', err);
      }
    };
    
    fetchDownloads();
    fetchDownloadCount();
  }, []);

  const handleDownloadFile = async (fileUrl, fileName) => {
    if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Increment the global counter on the remote database
      try {
        const res = await fetch('https://spaceportfolio-api.rajansudhan0.workers.dev/api/resume/increment-download', { method: 'POST' });
        const data = await res.json();
        if (data.success && data.count) {
          setDownloadCount(data.count);
        } else {
          setDownloadCount((prev) => prev + 1); // fallback visual increment
        }
      } catch (err) {
        console.error('Failed to increment global download count', err);
        setDownloadCount((prev) => prev + 1); // fallback visual increment
      }
    }
  };

  if (!loading && (!downloads || downloads.length === 0)) return null;

  return (
    <section className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 lg:p-8 mb-8 relative overflow-hidden shadow-2xl print:hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-30 pointer-events-none"></div>
      <div className="text-center mb-6 relative z-10">
        <h2 className="font-heading font-bold text-2xl lg:text-3xl text-text-primary mb-2">
          Download My Resume
        </h2>
        <p className="text-text-secondary">
          View or save my resume to learn more about my academic journey, research experience, and space ambitions.
        </p>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 gap-4 mb-6 animate-pulse">
           {[1, 2].map(i => (
             <div key={i} className="bg-white/5 border-2 border-white/10 rounded-xl p-6 h-40"></div>
           ))}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {downloads.map((option) => {
            const isPDF = option.title.toLowerCase().includes('pdf');
            const isDocx = option.title.toLowerCase().includes('docx') || option.title.toLowerCase().includes('word');
            
            const cardStyle = isPDF ? 'border-red-500/40 hover:border-red-500/80 shadow-[0_4px_20px_rgba(239,68,68,0.15)] bg-red-500/5' 
                            : isDocx ? 'border-blue-500/40 hover:border-blue-500/80 shadow-[0_4px_20px_rgba(59,130,246,0.15)] bg-blue-500/5' 
                            : 'border-white/10 hover:border-accent/50 bg-white/5';

            const iconStyle = isPDF ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                            : isDocx ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                            : 'bg-white/10 text-text-secondary';

            const btnClass = isPDF ? '!bg-red-600 hover:!bg-red-700 !text-white !border-0 shadow-lg shadow-red-600/20' 
                           : isDocx ? '!bg-blue-600 hover:!bg-blue-700 !text-white !border-0 shadow-lg shadow-blue-600/20' 
                           : 'border-white/20 text-text-primary hover:bg-white/5';

            return (
            <div
              key={option.id}
              className={`backdrop-blur-lg rounded-xl p-6 border-2 transition-all duration-300 group ${cardStyle} relative z-10`}
            >
              <div className="flex items-center space-x-4 mb-5">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0 ${iconStyle}`}>
                  <Icon name={option.organization || (isPDF ? "FileText" : isDocx ? "FileText" : "File")} size={28} />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-1">
                    {option.title}
                  </h3>
                  <p className="text-text-secondary text-sm overflow-hidden text-ellipsis line-clamp-2">
                    {option.description}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center justify-between mb-5 bg-black/20 p-3 rounded-lg border border-white/5">
                <span className="text-text-secondary text-sm font-medium flex items-center gap-2">
                   <Icon name="HardDrive" size={14} className="opacity-70" />
                   Size: <span className="text-text-primary">{option.badge || 'Unknown'}</span>
                </span>
                {option.display_order === 1 && (
                  <span className="bg-success/10 border border-success/20 text-success-light px-2.5 py-1 rounded-full text-xs font-bold tracking-wide uppercase">
                    Recommended
                  </span>
                )}
              </div>
              
              <Button
                variant="outline"
                fullWidth
                loading={isDownloading}
                onClick={() => handleDownloadFile(option.detailedContent, option.slug)}
                iconName="Download"
                iconPosition="left"
                className={`transition-all duration-300 transform group-hover:-translate-y-0.5 ${btnClass}`}
              >
                Download {option.title.split(' ')[0]}
              </Button>
            </div>
            );
          })}
        </div>
      )}

      {/* Additional Information */}
      <div className="bg-white/5 rounded-lg p-4 border border-white/10 relative z-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-accent/5 to-transparent opacity-20 pointer-events-none"></div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-2 md:space-y-0 relative z-10">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-text-secondary text-sm">
              <Icon name="Calendar" size={16} />
              <span>Last updated: {lastUpdated}</span>
            </div>
            <div className="flex items-center space-x-2 text-text-secondary text-sm">
              <Icon name="Download" size={16} />
              <span>{downloadCount.toLocaleString()} downloads</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              iconName="Share2"
              iconPosition="left"
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: 'Professional Resume',
                    text: 'Check out my professional resume',
                    url: window.location.href
                  });
                } else {
                  navigator.clipboard.writeText(window.location.href);
                  alert('Link copied to clipboard!');
                }
              }}
            >
              Share
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              iconName="Printer"
              iconPosition="left"
              onClick={() => window.print()}
            >
              Print
            </Button>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="mt-6 text-center relative z-10">
        <p className="text-text-secondary text-sm mb-2">
          Questions about my background or experience?
        </p>
        <Button
          variant="link"
          iconName="MessageCircle"
          iconPosition="left"
          onClick={() => window.location.href = '/contact'}
          className="text-accent hover:text-accent-light"
        >
          Get in touch
        </Button>
      </div>
    </section>
  );
};

export default DownloadSection;